<template>
    <k-page>
        <template v-slot:header>
            <profile-header/>
        </template>
        <k-container size="lg" class="pb-20">
            <a @click="$router.go(-1)" class="text-primary-500 mr-2 my-5 block">
                &#8592; {{ $t("goBack") }}
            </a>
            <k-alert class="mb-6" v-if="!$root.$data.investorProfile.active">
                <template slot="text">{{ $t("accountNotActive") }}</template>
                <template slot="description">
                    {{ $t("accountNotActiveDesc") }}
                </template>
            </k-alert>
            <div class="text-lg my-5 grid md:grid-cols-2 gap-5">
                <div>
                    <div class="mb-5" v-if="tranche && tranche.pictures && tranche.pictures.length > 0">
                        <img class="rounded object-scale-down"
                             :src="`/api/guest/download/${tranche.pictures[0]}`">
                    </div>
                    <div style="height: 100%">
                        <tranche-invest-form
                            class="mb-5"
                            v-if="!loading"
                            :offer="offer"
                            :tranche="tranche"
                            :investment="investment"
                            :investorActive="$root.$data.investorProfile.active"
                            ref="investForm"
                            @on-inquiry="submitInquiry"
                            @on-commit="commitFunds"
                            @on-investment-terms="openTerms"
                            @on-letter-of-waiver="openLetterOfWaiver"
                        />
                        <tranche-investment-details
                            v-if="!loading"
                            :investment="investment"
                            :currencySymbol="tranche.currencySymbol"
                            @on-cancel="unsubscribe"
                        />
                    </div>
                </div>
                <tranche-details
                    v-if="!loading"
                    :tranche="tranche"
                />
            </div>
            <k-tabs class="mt-10"
                    v-if="!loading"
                    tab-class="k-tab-profile"
                    active-tab-class="k-tab-profile-active"
                    content-class="py-5"
                    ref="tabs">
                <k-tab title="Summary">
                    <tranche-summary :tranche="tranche"></tranche-summary>
                </k-tab>
                <k-tab title="Borrower">
                    <div class="my-5 grid md:grid-cols-2 gap-5">
                        <k-panel class="border">
                            <template v-slot:header>
                                <k-panel-header>{{ borrower.companyName }}</k-panel-header>
                            </template>
                            <p class="leading-7" style="white-space: pre-line;">{{ borrower.description }}</p>
                        </k-panel>
                        <tranche-borrower 
                            :borrower="borrower" 
                            :trancheType="tranche.trancheType" 
                            :currencySymbol="tranche.currencySymbol"/>
                    </div>
                </k-tab>
                <k-tab title="Market" v-if="tranche.details.summary.market">
                    <tranche-market :tranche="tranche"/>
                </k-tab>
                <k-tab title="Documents">
                    <k-panel class="border">
                        <ul class="list-disc pl-5">
                            <li class="mb-4 text-primary-500" v-for="doc in attachments" :key="doc.fileReference">
                                <a :href="`/api/guest/download/${doc.fileReference}`" target="_blank"
                                >{{ doc.fileName | documentName }}</a>
                            </li>
                        </ul>
                        <div v-if="tranche.details.summary.embeddedVideoTitle && tranche.details.summary.embeddedVideoIframeCode">
                            <div class="mt-10 mb-2 font-bold">{{tranche.details.summary.embeddedVideoTitle}}</div>
                            <div class="iframe-video-wrapper" v-html="tranche.details.summary.embeddedVideoIframeCode"/>
                        </div>
                    </k-panel>
                </k-tab>
                <k-tab title="Payments">
                    <tranche-outstanding-payments
                        v-if="!loading"
                        :tranche="tranche"
                    />
                </k-tab>
                <k-tab title="Capital call" v-if="tranche.capitalCallEnabled">
                    <tranche-capital-call
                        v-if="!loading"
                        :tranche="tranche"
                        :investment="investment"
                        :requests="capitalCallRequests"
                        @on-request="refreshCapitalCallRequests"
                        @on-cancel="refreshCapitalCallRequests"
                        class="mb-6"
                    />
                </k-tab>
            </k-tabs>
        </k-container>
    </k-page>
</template>

<script>
import ProfileHeader from '@investor/components/profile/ProfileHeader';
import TrancheInvestForm from '@product-common/components/tranches/TrancheInvestForm';
import TrancheInvestmentDetails from '@product-common/components/tranches/TrancheInvestmentDetails';
import TrancheDetails from '@product-common/components/tranches/TrancheDetails';
import TrancheBorrower from '@product-common/components/tranches/TrancheBorrower';
import TrancheOutstandingPayments from '@product-common/components/tranches/TrancheOutstandingPayments';
import KTabs from "@common/components/tabs/KTabs";
import KTab from "@common/components/tabs/KTab";
import TrancheSummary from "@product-common/components/tranches/TrancheSummary";
import TrancheMarket from "@product-common/components/tranches/TrancheMarket";
import TrancheCapitalCall from "../../../common/components/tranches/TrancheCapitalCall";

export default {
    components: {
        ProfileHeader,
        TrancheInvestForm,
        TrancheInvestmentDetails,
        TrancheDetails,
        TrancheBorrower,
        TrancheOutstandingPayments,
        KTabs,
        KTab,
        TrancheSummary,
        TrancheMarket,
        TrancheCapitalCall,
    },
    data() {
        return {
            loading: true,
            trancheUuid: null,
            offer: null,
            investment: {},
            tranche: {},
            borrower: {},
            attachments: [],
            capitalCallRequests: []
        };
    },
    created() {
        this.trancheUuid = this.$route.params.trancheUuid;
    },
    async mounted() {
        await this.refresh();
    },
    methods: {
        async refresh() {
            this.loading = true;
            try {
                this.offer = null;
                const resp = await this.$post('tranche', {trancheUuid: this.trancheUuid});
                this.tranche = resp.tranche;
                this.investment = resp.investment;
                this.borrower = resp.borrower;
                this.attachments = resp.attachments;
                await this.refreshCapitalCallRequests();
            } finally {
                this.loading = false;
            }
        },
        async submitInquiry(inquiryForm) {
            let resp = await this.$post('tranche/subscription-offer', inquiryForm);
            this.offer = resp.offer;
            this.$trackGAEvent('investing', 'createSubmit');
        },
        async refreshCapitalCallRequests() {
            try {
                this.capitalCallRequests = (await this.$post('tranche/capital-call-requests', {trancheUuid: this.trancheUuid})).capitalCallRequests;
            } catch (e) {
                console.error('Failed to get capital call requests', e)
            }
        },
        async submitCapitalCallRequest() {
            await this.refreshCapitalCallRequests()
        },
        async commitFunds(commitForm) {
            await this.$post('tranche/subscribe', commitForm);
            await this.refresh();
            await this.$fetchInvestorProfile();
            this.$trackGAEvent('investing', 'createSubmitSuccess');
        },
        async unsubscribe() {
            await this.$post('tranche/unsubscribe', {trancheUuid: this.trancheUuid});
            await this.refresh();
            await this.$fetchInvestorProfile();
        },
        async openTerms(form) {
            window.open(`/api/investor/tranche/note-of-acceptance-preview?trancheUuid=${form.trancheUuid}&amount=${form.amount}`, '_blank');
        },
        async openLetterOfWaiver(form) {
            window.open(`/api/investor/tranche/letter-ov-waiver-preview?trancheUuid=${form.trancheUuid}`, '_blank');
        }
    }
};
</script>


